import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { InputWithMask } from 'ui-components/components/input/with-mask';
import { Text, TextType } from 'ui-components/components/text';

import { CITY, COMPANY, LINE1, POSTAL_CODE } from '../../../../../address/constants';
import countryNameMessages from '../../../../../components/country-names.messages';
import { AT, BE, DE, ES, FR, GB, HU, IE, IT, LU, NL, PT } from '../../../../../constants/country-codes';
import { TAX_IDENTIFIER_FIELD, TaxIdCountryConfig } from '../../constants/tax-identifier';
import { useTaxDataContext } from '../../context/tax-data-context-provider';
import { useTaxFormContext } from '../../context/tax-form-context-provider';
import { useTaxIdConfig } from '../../hooks/use-tax-id-config';
import { InvoiceAddress } from '../invoice-address/invoice-address';
import { messages } from './messages';
import style from './tax-identifier-form.module.less';

export const requiredFields = [COMPANY, LINE1, CITY, POSTAL_CODE, TAX_IDENTIFIER_FIELD];

const issuingAuthorityList = [GB, IE, FR, DE, IT, NL, BE, LU, PT, HU, AT, ES];

/**
 * @param {object} props
 * @param {string} [props.className] - Optional additional CSS class name to override wrapper styles
 * @returns {JSX.Element}
 */
export const TaxIdentifierForm = ({ className }) => {
  const intl = useIntl();
  const { taxData, isTaxDataAvailable } = useTaxDataContext();
  const {
    invoiceAddress,
    setInvoiceAddressField,
    taxIdentifier,
    setTaxIdentifier,
    issuingAuthority,
    setIssuingAuthorityValue,
    fieldsWithError,
    validateFormField,
  } = useTaxFormContext();
  const { isInvoiceAddressRequired, shouldShowIssuingAuthorityList, taxIdName, taxIdMask, defaultIssuingAuthority, isTaxIdEditAllowed } =
    useTaxIdConfig(issuingAuthority);

  const effectiveIssuingAuthority = issuingAuthority || defaultIssuingAuthority;
  const countryName = countryNameMessages[issuingAuthority];
  const issuingAuthorityPlaceholder = issuingAuthority ? (
    <FormattedMessage {...countryName} />
  ) : (
    <FormattedMessage {...messages.issuingAuthorityPlaceholder} />
  );

  const invalidTaxIdMessage = useMemo(() => intl.formatMessage(messages.invalidTaxIdError, { taxIdName }), [intl, taxIdName]);

  const originalTaxIdRef = useRef(null);

  const handleTaxIdFocus = useCallback(() => {
    if (taxData?.taxIdentifier && isTaxIdEditAllowed && taxIdentifier === taxData.taxIdentifier) {
      originalTaxIdRef.current = taxData.taxIdentifier;
      setTaxIdentifier('');
    }
  }, [taxData?.taxIdentifier, isTaxIdEditAllowed, taxIdentifier, setTaxIdentifier]);

  const handleTaxIdBlur = useCallback(
    (field, value) => {
      if (!value && originalTaxIdRef.current) {
        setTaxIdentifier(originalTaxIdRef.current);
        validateFormField(field, originalTaxIdRef.current);
        originalTaxIdRef.current = null;
      } else {
        originalTaxIdRef.current = null;
        validateFormField(field, taxIdentifier);
      }
    },
    [setTaxIdentifier, validateFormField, taxIdentifier],
  );

  const showTaxIdentifier = !shouldShowIssuingAuthorityList || issuingAuthority;

  return (
    <FlexColumn className={classNames(style.wrapper, className)} gap={Gap.Space24}>
      <FlexColumn gap={Gap.Space8}>
        {shouldShowIssuingAuthorityList ? (
          <FlexColumn>
            <Text textType={TextType.Label} value={messages.issuingAuthorityLabel} />
            <DropdownMenu disabled={isTaxDataAvailable} dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
              <DropdownMenuToggler>{issuingAuthorityPlaceholder}</DropdownMenuToggler>
              {issuingAuthorityList.map((issuingAuthorityItem) => (
                <DropdownMenuItem
                  key={issuingAuthorityItem}
                  disabled={isTaxDataAvailable}
                  name={issuingAuthorityItem}
                  onClick={setIssuingAuthorityValue}
                  selected={issuingAuthority === issuingAuthorityItem}
                >
                  <FormattedMessage {...countryNameMessages[issuingAuthorityItem]} />
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </FlexColumn>
        ) : null}
        {showTaxIdentifier ? (
          <section>
            <Text textType={TextType.Label} value={taxIdName} />
            {taxData?.taxIdentifier && !isTaxIdEditAllowed ? (
              <Input disabled value={taxData?.taxIdentifier} version={version2} />
            ) : (
              <InputWithMask
                hideMask={TaxIdCountryConfig[effectiveIssuingAuthority]?.hideMask}
                inlineMessage={fieldsWithError?.[TAX_IDENTIFIER_FIELD] ? invalidTaxIdMessage : null}
                inlineMessageType={INLINE_MESSAGE_TYPES.error}
                keepMaskCharacters={TaxIdCountryConfig[effectiveIssuingAuthority]?.keepMaskCharacters}
                mask={taxIdMask}
                name={TAX_IDENTIFIER_FIELD}
                onBlur={handleTaxIdBlur}
                onChange={setTaxIdentifier}
                onFocus={handleTaxIdFocus}
                value={taxIdentifier}
              />
            )}
          </section>
        ) : null}
      </FlexColumn>

      {isInvoiceAddressRequired ? (
        <InvoiceAddress
          address={invoiceAddress}
          fieldsWithError={fieldsWithError}
          onBlur={validateFormField}
          onChange={setInvoiceAddressField}
          requiredFields={requiredFields}
        />
      ) : null}
    </FlexColumn>
  );
};
